import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/use-organization";
import { PageLoadingSpinner } from "@/components/PageLoadingSpinner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { BarChart3, TrendingUp, Clock, DollarSign, Calendar, Lightbulb, CalendarClock } from "lucide-react";
import ScheduleHeatMap from "@/components/ScheduleHeatMap";
import GapAnalysis from "@/components/GapAnalysis";
import ScheduleRecommendations from "@/components/ScheduleRecommendations";
import ReupProjections from "@/components/ReupProjections";
import { format, subWeeks, startOfWeek, endOfWeek } from "date-fns";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { UpgradeCard } from "@/components/UpgradeCard";

// SECURITY: Storage key prefix for user-specific data
const MAX_HOURS_STORAGE_KEY_PREFIX = "insights-max-hours-per-week_";

// Clear max hours setting for a specific user (exported for logout cleanup)
export function clearInsightsMaxHours(userId: string) {
  localStorage.removeItem(`${MAX_HOURS_STORAGE_KEY_PREFIX}${userId}`);
}

const Insights = () => {
  const { organization } = useOrganization();
  const { hasFeature, loading: featureLoading } = useFeatureFlags();
  const isFeatureEnabled = !featureLoading && hasFeature("pulse_check");

  const [weeksBack, setWeeksBack] = useState<string>("4");
  const [userId, setUserId] = useState<string | null>(null);
  const [maxHoursPerWeek, setMaxHoursPerWeek] = useState<number>(40);

  // Load user ID and their max hours setting
  useEffect(() => {
    const loadUserSettings = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        const saved = localStorage.getItem(`${MAX_HOURS_STORAGE_KEY_PREFIX}${user.id}`);
        if (saved) {
          setMaxHoursPerWeek(parseInt(saved));
        }
      }
    };
    loadUserSettings();
  }, []);

  // Persist maxHoursPerWeek to localStorage (user-specific)
  useEffect(() => {
    if (userId) {
      localStorage.setItem(`${MAX_HOURS_STORAGE_KEY_PREFIX}${userId}`, maxHoursPerWeek.toString());
    }
  }, [maxHoursPerWeek, userId]);

  // Fetch sessions for the analysis period (historical only)
  const { data: sessions = [], isLoading: sessionsLoading } = useQuery({
    queryKey: ["insights-sessions", weeksBack, organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];

      const startDate = subWeeks(new Date(), parseInt(weeksBack));
      const endDate = new Date();

      const { data, error } = await supabase
        .from("sessions")
        .select(
          `
          id,
          date,
          cancelled,
          completed_at,
          client_id,
          clients (
            name,
            client_type
          )
        `
        )
        .eq("organization_id", organization.id)
        .gte("date", startDate.toISOString())
        .lte("date", endDate.toISOString())
        .order("date", { ascending: true });

      if (error) {
        console.error("Error fetching sessions:", error);
        return [];
      }
      return data || [];
    },
    enabled: !!organization?.id,
  });

  // Fetch packages for rate calculation
  const { data: packages = [] } = useQuery({
    queryKey: ["insights-packages", organization?.id],
    queryFn: async () => {
      if (!organization?.id) return [];

      const { data, error } = await supabase
        .from("packages")
        .select("rate, status")
        .eq("organization_id", organization.id)
        .not("rate", "is", null);

      if (error) {
        console.error("Error fetching packages:", error);
        return [];
      }
      return data || [];
    },
    enabled: !!organization?.id,
  });

  // Calculate average rate
  const averageRate =
    packages.length > 0
      ? packages.reduce((sum, p) => sum + (Number(p.rate) || 0), 0) / packages.length
      : 75; // Default fallback

  // Filter out cancelled sessions for analysis
  const validSessions = sessions.filter((s) => !s.cancelled);

  // Calculate summary stats
  const totalSessions = validSessions.length;
  const weeksAnalyzed = parseInt(weeksBack);
  const avgSessionsPerWeek = weeksAnalyzed > 0 ? (totalSessions / weeksAnalyzed).toFixed(1) : "0";

  // Calculate utilization based on user's max hours preference
  const actualHoursPerWeek = weeksAnalyzed > 0 ? totalSessions / weeksAnalyzed : 0; // 1 hour per session
  const utilizationRate =
    maxHoursPerWeek > 0 ? ((actualHoursPerWeek / maxHoursPerWeek) * 100).toFixed(0) : "0";

  const dateRange = {
    start: format(subWeeks(new Date(), parseInt(weeksBack)), "MMM d"),
    end: format(new Date(), "MMM d, yyyy"),
  };

  if (sessionsLoading) {
    return (
      <>
        <PageLoadingSpinner message="Loading schedule data..." />
      </>
    );
  }

  // Feature gate check - after all hooks
  if (!isFeatureEnabled) {
    return (
      <div className="py-12">
        <UpgradeCard
          featureKey="pulse_check"
          feature="Pulse Check"
          description="Schedule analytics, gap detection, and re-up insights to optimize your week."
        />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="space-y-1">
            <h1 className="font-sans text-2xl font-semibold text-foreground tracking-tight flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-primary" />
              Pulse Check
            </h1>
            <p className="text-sm text-muted-foreground">
              Analyze your schedule patterns and find optimization opportunities.
            </p>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Max hrs/week</span>
              <Input
                type="number"
                min={10}
                max={80}
                value={maxHoursPerWeek}
                onChange={(e) => setMaxHoursPerWeek(Math.max(10, Math.min(80, parseInt(e.target.value) || 40)))}
                className="w-[70px] h-9 font-mono tabular-nums"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Analyze last</span>
              <Select value={weeksBack} onValueChange={setWeeksBack}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">4 weeks</SelectItem>
                  <SelectItem value="8">8 weeks</SelectItem>
                  <SelectItem value="12">12 weeks</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border border-border bg-card shadow-none rounded-lg">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <Calendar className="h-4 w-4" />
                Total sessions
              </div>
              <div className="text-[2rem] font-display font-semibold font-mono tabular-nums leading-none text-foreground">
                {totalSessions}
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border bg-card shadow-none rounded-lg">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <TrendingUp className="h-4 w-4" />
                Avg / week
              </div>
              <div className="text-2xl font-semibold font-mono tabular-nums text-foreground">
                {avgSessionsPerWeek}
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border bg-card shadow-none rounded-lg">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <Clock className="h-4 w-4" />
                Utilization
              </div>
              <div className="text-2xl font-semibold font-mono tabular-nums text-foreground">
                {utilizationRate}%
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border bg-card shadow-none rounded-lg">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                <DollarSign className="h-4 w-4" />
                Avg rate
              </div>
              <div className="text-2xl font-semibold font-mono tabular-nums text-foreground">
                ${averageRate.toFixed(0)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Date range info */}
        <p className="text-sm text-muted-foreground">
          Analyzing data from <span className="font-mono tabular-nums">{dateRange.start}</span> to{" "}
          <span className="font-mono tabular-nums">{dateRange.end}</span>.
        </p>

        {/* Main content tabs */}
        <Tabs defaultValue="heatmap" className="space-y-4">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
            <TabsTrigger value="heatmap" className="gap-2">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Heat map</span>
              <span className="sm:hidden">Map</span>
            </TabsTrigger>
            <TabsTrigger value="gaps" className="gap-2">
              <TrendingUp className="h-4 w-4" />
              <span>Growth</span>
            </TabsTrigger>
            <TabsTrigger value="reups" className="gap-2">
              <CalendarClock className="h-4 w-4" />
              <span>Re-ups</span>
            </TabsTrigger>
            <TabsTrigger value="recommendations" className="gap-2">
              <Lightbulb className="h-4 w-4" />
              <span>Tips</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="heatmap">
            <ScheduleHeatMap sessions={validSessions} weeksBack={parseInt(weeksBack)} />
          </TabsContent>

          <TabsContent value="gaps">
            <GapAnalysis 
              sessions={validSessions} 
              weeksBack={parseInt(weeksBack)} 
              averageRate={averageRate}
              maxHoursPerWeek={maxHoursPerWeek}
            />
          </TabsContent>

          <TabsContent value="reups">
            <ReupProjections />
          </TabsContent>

          <TabsContent value="recommendations">
            <ScheduleRecommendations
              sessions={validSessions}
              weeksBack={parseInt(weeksBack)}
              averageRate={averageRate}
              maxHoursPerWeek={maxHoursPerWeek}
            />
          </TabsContent>
        </Tabs>

        {/* Quick access tip */}
        <Card className="border border-border bg-card shadow-none rounded-lg border-l-2 border-l-primary">
          <CardContent className="pt-5 pb-4">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Tip:</span> Schedule insights (heat map, growth, tips) are also available in the{" "}
              <span className="font-medium text-foreground">Calendar</span> sidebar under the Insights tab.
              Re-up projections are also shown on the{" "}
              <span className="font-medium text-foreground">Clients</span> page.
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default Insights;
